/** Card id → how many copies. */

export type CardCounts = Record<string, number>;

export function emptyCounts(): CardCounts {
  return {};
}

export function countsFromIds(ids: readonly string[]): CardCounts {
  const out: CardCounts = {};
  for (const id of ids) {
    out[id] = (out[id] ?? 0) + 1;
  }
  return out;
}

export function expandCounts(counts: CardCounts): string[] {
  const out: string[] = [];
  for (const [id, n] of Object.entries(counts)) {
    const qty = Math.max(0, Math.floor(n));
    for (let i = 0; i < qty; i++) out.push(id);
  }
  return out;
}

export function totalCount(counts: CardCounts): number {
  let t = 0;
  for (const n of Object.values(counts)) t += Math.max(0, Math.floor(n));
  return t;
}

export function ownedCount(counts: CardCounts, id: string): number {
  return Math.max(0, Math.floor(counts[id] ?? 0));
}

export function uniqueIds(counts: CardCounts): string[] {
  return Object.keys(counts).filter((id) => ownedCount(counts, id) > 0);
}

/** Return a new map with +delta copies of id (floored at 0). */
export function addCount(
  counts: CardCounts,
  id: string,
  delta = 1,
): CardCounts {
  const next = { ...counts };
  const n = ownedCount(next, id) + delta;
  if (n <= 0) delete next[id];
  else next[id] = n;
  return next;
}

/** Ensure every starter id has at least 1 copy. */
export function mergeMinimum(
  counts: CardCounts,
  minimum: readonly string[],
): CardCounts {
  const next = { ...counts };
  for (const id of minimum) {
    if (ownedCount(next, id) < 1) next[id] = 1;
  }
  return next;
}

/** Cap every entry at maxCopies (drops non-positive). */
export function clampCounts(counts: CardCounts, maxCopies: number): CardCounts {
  const out: CardCounts = {};
  for (const [id, n] of Object.entries(counts)) {
    const v = Math.min(maxCopies, Math.max(0, Math.floor(n)));
    if (v > 0) out[id] = v;
  }
  return out;
}
